import { useTranslation } from 'react-i18next';
import type { AdminUser, RoleName, UserStatus } from '@/modules/user-management/lib/admin.schema';
import type { UpdateUserInput } from '@/modules/user-management/lib/management.service';
import { useAdminUserStore } from '@/modules/user-management/lib/admin-user.store';
import { Drawer } from '@/shared/components/Drawer';
import { EditableField } from '@/shared/components/EditableField';
import { env } from '@/shared/lib/env';
import { ModalFooter } from '@/shared/components/ModalFooter';
import { PillSelect } from '@/shared/components/PillSelect';
import { Toggle } from '@/shared/components/Toggle';

import { ADMIN_ROLES, USER_STATUSES } from '../../../lib/adminUsers.constants';
import { formatAdminDate } from '../../../lib/adminUsers.format';
import { getCreateIssue } from '../../../lib/adminUsers.validation';

type UpdatableField = 'new_email' | 'display_name' | 'phone';

const buildInput = (
  field: UpdatableField,
  newValue: string,
  userEmail: string,
): UpdateUserInput => ({ email: userEmail, [field]: newValue || null });

interface AdminUserDetailsModalProps {
  allOrganizations: string[];
  user?: AdminUser;
}

export const AdminUserDetailsModal = ({ allOrganizations, user }: AdminUserDetailsModalProps) => {
  const { t } = useTranslation('userManagement');
  const modal = useAdminUserStore((s) => s.modal);
  const form = useAdminUserStore((s) => s.form);
  const pendingAction = useAdminUserStore((s) => s.pendingAction);
  const error = useAdminUserStore((s) => s.error);
  const { setFormField, closeModal, act, openDeleteConfirm } = useAdminUserStore();

  if (!modal) {
    return null;
  }

  const isCreate = modal.type === 'create';
  const isPending = (type: string) => pendingAction === type;
  const createIssue = isCreate ? getCreateIssue(form) : null;
  const createIssueMessage = createIssue
    ? t(`detailsModal.createIssues.${createIssue}`, { min: env.VITE_PASSWORD_MIN })
    : undefined;

  return (
    <Drawer
      accent="sky"
      title={isCreate ? t('detailsModal.titleCreate') : t('detailsModal.titleEdit')}
      widthClassName="max-w-2xl"
      footer={
        isCreate ? (
          <ModalFooter
            isCreate
            onClose={closeModal}
            error={createIssueMessage ?? error}
            onCreate={() => {
              act({ type: 'createUser', input: form });
            }}
            createLabel={t('detailsModal.createUser')}
            creatingLabel={t('detailsModal.creating')}
            isCreating={isPending('createUser')}
            createDisabled={createIssue !== null}
            size="sm"
          />
        ) : (
          <ModalFooter
            isCreate={false}
            onClose={closeModal}
            error={error}
            onDelete={() => {
              if (user) {
                openDeleteConfirm(user.email);
              }
            }}
            deleteLabel={t('detailsModal.deleteUser')}
            isDeleting={isPending('delete')}
          />
        )
      }
      onClose={closeModal}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2 grid-cols-1">
          <EditableField
            isSaving={isPending('updateUser')}
            label={t('detailsModal.fields.email')}
            onChange={(value) => {
              setFormField('email', value);
            }}
            onSave={
              !isCreate && user
                ? (value) => {
                    act({ type: 'updateUser', input: buildInput('new_email', value, user.email) });
                  }
                : undefined
            }
            original={user?.email ?? ''}
            type="email"
            value={form.email}
          />

          {isCreate ? (
            <>
              <div className="flex items-center justify-between gap-3 py-2">
                <span className="text-sm">{t('detailsModal.withPassword')}</span>
                <Toggle
                  checked={form.withPassword}
                  onChange={(checked) => {
                    setFormField('withPassword', checked);
                  }}
                />
              </div>

              {form.withPassword ? (
                <>
                  <EditableField
                    label={t('detailsModal.fields.password')}
                    onChange={(value) => {
                      setFormField('password', value);
                    }}
                    type="password"
                    value={form.password}
                  />
                  <EditableField
                    label={t('detailsModal.fields.confirmPassword')}
                    onChange={(value) => {
                      setFormField('confirmPassword', value);
                    }}
                    type="password"
                    value={form.confirmPassword}
                  />
                </>
              ) : (
                <p className="m-0 text-sm text-ink-subtle">{t('detailsModal.noPasswordHint')}</p>
              )}
            </>
          ) : null}

          <EditableField
            isSaving={isPending('updateUser')}
            label={t('detailsModal.fields.displayName')}
            onChange={(value) => {
              setFormField('display_name', value);
            }}
            onSave={
              !isCreate && user
                ? (value) => {
                    act({
                      type: 'updateUser',
                      input: buildInput('display_name', value, user.email),
                    });
                  }
                : undefined
            }
            original={user?.display_name ?? ''}
            value={form.display_name}
          />

          <EditableField
            isSaving={isPending('updateUser')}
            label={t('detailsModal.fields.phone')}
            onChange={(value) => {
              setFormField('phone', value);
            }}
            onSave={
              !isCreate && user
                ? (value) => {
                    act({ type: 'updateUser', input: buildInput('phone', value, user.email) });
                  }
                : undefined
            }
            original={user?.phone ?? ''}
            type="tel"
            value={form.phone}
          />
        </div>

        <section className="flex itesm-start flex-col gap-4">
          {!isCreate && user ? (
            <>
              <div>
                <p className="eyebrow">{t('detailsModal.createdAt')}</p>
                <section className="m-0">
                  {formatAdminDate(user.created_at, t('dateNotProvided'))}
                </section>
              </div>
              <PillSelect
                color="sky"
                label={t('detailsModal.statusLabel')}
                loading={isPending('updateUser')}
                onChange={(value) => {
                  act({ type: 'updateStatus', email: user.email, status: value as UserStatus });
                }}
                options={USER_STATUSES.map((status) => ({
                  label: t(`statuses.${status}`),
                  value: status,
                }))}
                value={user.status}
              />
            </>
          ) : null}

          <PillSelect
            color="sky"
            label={
              isCreate ? t('detailsModal.organizationLabel') : t('detailsModal.organizationsLabel')
            }
            loading={!isCreate && isPending('toggleOrg')}
            onChange={(value) => {
              if (isCreate) {
                setFormField('organization', value);
              } else if (user) {
                act({
                  type: 'toggleOrg',
                  email: user.email,
                  slug: value,
                  remove: user.organizations.includes(value),
                });
              }
            }}
            options={allOrganizations.map((slug) => ({ label: slug, value: slug }))}
            value={isCreate ? form.organization : (user?.organizations ?? [])}
          />

          <PillSelect
            color="sky"
            label={t('detailsModal.roleLabel')}
            loading={!isCreate && isPending('setRole')}
            onChange={(value) => {
              if (isCreate) {
                setFormField('role', value as RoleName);
              } else if (user) {
                act({
                  type: 'setRole',
                  email: user.email,
                  role: value as RoleName,
                });
              }
            }}
            options={ADMIN_ROLES.map((role) => ({ label: t(`roles.${role}`), value: role }))}
            value={isCreate ? form.role : (user?.role ?? '')}
          />
        </section>
      </div>
    </Drawer>
  );
};
